import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { ImageWithFallback } from './figma/ImageWithFallback';
import { Truck, Globe, FileCheck, Shield, Clock, Users, ArrowRight } from 'lucide-react';

interface ServicesPageProps {
  onPageChange: (page: string) => void;
}

export function ServicesPage({ onPageChange }: ServicesPageProps) {
  const mainServices = [
    {
      icon: <Globe className="w-12 h-12 text-[#fe2b32]" />,
      title: "Global Trading",
      description: "Comprehensive international scrap trading services connecting suppliers and buyers worldwide.",
      features: [
        "International market access",
        "Competitive pricing strategies",
        "Risk management solutions",
        "Currency hedging options"
      ]
    },
    {
      icon: <Truck className="w-12 h-12 text-[#ffc533]" />,
      title: "Logistics & Shipping",
      description: "End-to-end logistics solutions ensuring safe and timely delivery of materials globally.",
      features: [
        "Container shipping management",
        "Customs clearance assistance",
        "Door-to-door delivery",
        "Real-time tracking systems"
      ]
    },
    {
      icon: <FileCheck className="w-12 h-12 text-[#ff41a5]" />,
      title: "Quality Inspection",
      description: "Rigorous quality control and inspection services to ensure material specifications are met.",
      features: [
        "Pre-shipment inspections",
        "Laboratory testing",
        "Certificates of analysis",
        "Third-party verification"
      ]
    },
    {
      icon: <Shield className="w-12 h-12 text-[#fe2b32]" />,
      title: "Documentation",
      description: "Complete documentation services for international trade compliance and transparency.",
      features: [
        "Export/import documentation",
        "Certificates of origin",
        "Insurance arrangements",
        "Regulatory compliance"
      ]
    }
  ];

  const additionalServices = [
    {
      icon: <Clock className="w-8 h-8 text-[#ffc533]" />,
      title: "24/7 Customer Support",
      description: "Round-the-clock customer service to address inquiries and provide assistance."
    },
    {
      icon: <Users className="w-8 h-8 text-[#ff41a5]" />,
      title: "Consultation Services",
      description: "Expert consultation on market trends, pricing, and material specifications."
    },
    {
      icon: <Truck className="w-8 h-8 text-[#fe2b32]" />,
      title: "Warehousing Solutions",
      description: "Secure storage facilities for temporary holding and sorting of materials."
    }
  ];

  const processSteps = [
    {
      step: "01",
      title: "Initial Inquiry",
      description: "Contact us with your material requirements or selling intentions"
    },
    {
      step: "02", 
      title: "Quotation",
      description: "Receive competitive quotes based on current market conditions"
    },
    {
      step: "03",
      title: "Agreement",
      description: "Finalize terms, pricing, and delivery specifications"
    },
    {
      step: "04",
      title: "Quality Check",
      description: "Comprehensive inspection and quality verification process"
    },
    {
      step: "05",
      title: "Documentation",
      description: "Complete all required export/import and compliance paperwork"
    },
    {
      step: "06",
      title: "Shipment",
      description: "Secure packaging and global shipping with full tracking"
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-r from-gray-900 to-gray-800 text-white">
        <div className="container mx-auto px-4">
          <div className="text-center">
            <h1 className="text-5xl font-bold mb-6">Our Services</h1>
            <p className="text-xl max-w-3xl mx-auto text-gray-200">
              Comprehensive scrap trading services designed to meet the diverse needs 
              of our global clientele with excellence and reliability.
            </p>
          </div>
        </div>
      </section>

      {/* Main Services Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">Core Services</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              End-to-end solutions for all your scrap trading requirements
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {mainServices.map((service, index) => (
              <Card key={index} className="hover:shadow-lg transition-shadow">
                <CardContent className="p-8">
                  <div className="mb-6 flex justify-center md:justify-start">
                    {service.icon}
                  </div>
                  <h3 className="text-2xl font-semibold mb-4">{service.title}</h3>
                  <p className="text-gray-600 mb-6">{service.description}</p>
                  <ul className="space-y-2">
                    {service.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-center">
                        <div className="w-2 h-2 bg-[#fe2b32] rounded-full mr-3"></div>
                        <span className="text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">Our Trading Process</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              A streamlined 6-step process ensuring smooth transactions from inquiry to delivery
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {processSteps.map((step, index) => (
              <Card key={index} className="relative hover:shadow-lg transition-shadow">
                <CardContent className="p-6 text-center">
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 w-8 h-8 bg-[#fe2b32] text-white rounded-full flex items-center justify-center font-bold text-sm">
                    {step.step}
                  </div>
                  <div className="pt-4">
                    <h3 className="text-xl font-semibold mb-3">{step.title}</h3>
                    <p className="text-gray-600">{step.description}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Additional Services */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">Additional Services</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Value-added services to enhance your trading experience
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {additionalServices.map((service, index) => (
              <Card key={index} className="hover:shadow-lg transition-shadow">
                <CardContent className="p-6 text-center">
                  <div className="mb-4 flex justify-center">{service.icon}</div>
                  <h3 className="text-xl font-semibold mb-3">{service.title}</h3>
                  <p className="text-gray-600">{service.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Logistics Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold mb-6">Global Logistics Network</h2>
              <p className="text-lg text-gray-600 mb-6">
                Our extensive logistics network ensures reliable and cost-effective transportation 
                of materials to destinations worldwide. We partner with leading shipping lines and 
                logistics providers to offer flexible solutions.
              </p>
              <div className="space-y-4">
                <div className="flex items-center">
                  <div className="w-2 h-2 bg-[#fe2b32] rounded-full mr-3"></div>
                  <span>Major ports coverage in Middle East, Asia, and Europe</span>
                </div>
                <div className="flex items-center">
                  <div className="w-2 h-2 bg-[#ffc533] rounded-full mr-3"></div>
                  <span>Container shipping and bulk cargo solutions</span>
                </div>
                <div className="flex items-center">
                  <div className="w-2 h-2 bg-[#ff41a5] rounded-full mr-3"></div>
                  <span>Inland transportation and last-mile delivery</span>
                </div>
                <div className="flex items-center">
                  <div className="w-2 h-2 bg-[#fe2b32] rounded-full mr-3"></div>
                  <span>Real-time shipment tracking and updates</span>
                </div>
              </div>
            </div>
            <div>
              <ImageWithFallback 
                src="https://images.unsplash.com/photo-1725449264087-28926bc1a610?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxzaGlwcGluZyUyMGxvZ2lzdGljcyUyMGNvbnRhaW5lcnxlbnwxfHx8fDE3NTYzMjE3MDl8MA&ixlib=rb-4.1.0&q=80&w=1080"
                alt="Shipping and Logistics" 
                className="rounded-lg shadow-lg w-full h-80 object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Quality Standards */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">Quality Standards</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We adhere to the highest international standards for material quality and service excellence
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <Card className="p-6 text-center border-t-4 border-[#fe2b32]">
              <h3 className="font-semibold mb-2">ISO Certified</h3>
              <p className="text-gray-600 text-sm">ISO 9001:2015 quality management system certification</p>
            </Card>
            <Card className="p-6 text-center border-t-4 border-[#ffc533]">
              <h3 className="font-semibold mb-2">ISRI Standards</h3>
              <p className="text-gray-600 text-sm">Compliance with Institute of Scrap Recycling Industries guidelines</p>
            </Card>
            <Card className="p-6 text-center border-t-4 border-[#ff41a5]">
              <h3 className="font-semibold mb-2">Environmental Compliance</h3>
              <p className="text-gray-600 text-sm">Adherence to international environmental regulations</p>
            </Card>
            <Card className="p-6 text-center border-t-4 border-[#fe2b32]">
              <h3 className="font-semibold mb-2">Dubai Municipality</h3>
              <p className="text-gray-600 text-sm">Licensed waste management and trading operations</p>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-[#fe2b32] to-[#ff41a5] text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-6">Ready to Get Started?</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Let our experienced team help you with your scrap trading requirements. 
            Contact us today for personalized service and competitive quotes.
          </p>
          <Button 
            size="lg" 
            variant="secondary"
            className="bg-white text-[#fe2b32] hover:bg-gray-100"
            onClick={() => onPageChange('contact')}
          >
            Contact Our Team
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </section>
    </div>
  );
}